// WHAT: Settings page for managing TimeTracQ workspaces and aspects
// WHY:  Provides interface for configuring workspace structure, aspect settings, and daily capacity
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: Custom hooks, UI components, workspace and aspect commands
// RISKS:
//   - NOTE: Workspace ordering is hardcoded - may not match user expectations
//   - NOTE: Modal state management is complex - may have race conditions
//   - NOTE: Config loading may fail silently
//   - NOTE: No validation for aspect/workspace names - may allow invalid characters

import { useState, useEffect } from "react";

import { Button } from "@/components/ui";

import { WorkspaceCard, AddWorkspaceModal, AddAspectModal } from "./components";
import {
  useWorkspaceConfig,
  useAspectManagement,
  useWorkspaceExpansion,
  useWorkspaceCreation,
} from "./hooks";

/**
 * WHAT:
 *   Settings page component that provides interface for managing TimeTracQ
 *   workspaces, aspects, and global settings.
 *
 * WHY:
 *   Enables users to configure workspace structure, aspect properties, and
 *   daily capacity settings for time tracking.
 *
 * BEHAVIOR:
 *   - Loads workspace configuration on mount
 *   - Displays workspaces and aspects in expandable cards
 *   - Supports adding/removing workspaces and aspects
 *   - Allows editing aspect properties
 *   - Saves configuration to backend
 *   - Manages modal states for add operations
 *
 *
 * RISKS:
 *   - NOTE: Workspace ordering is hardcoded - may not match user expectations
 *   - NOTE: Modal state management is complex - may have race conditions
 *   - NOTE: Config loading may fail silently
 *   - NOTE: No validation for aspect/workspace names - may allow invalid characters
 *
 * @returns {JSX.Element} TimeTracQ workspace management component
 *
 */
export function TimeTracQAppSettingsWorkspaceManagement() {
  const {
    config,
    loadConfig,
    loading,
    loadWorkspaceNames,
    message,
    saveConfig,
    saving,
    setConfig,
    setMessage,
    setSaving,
    workspaceNames,
  } = useWorkspaceConfig();

  const {
    addAspect,
    toggleAspectBoolean,
    updateAspectColor,
    updateAspectHours,
    updateAspectStatus,
  } = useAspectManagement(config, setConfig, setMessage, setSaving);

  const { expandedWorkspaces, expandWorkspace, toggleAllWorkspaces, toggleWorkspaceExpansion } =
    useWorkspaceExpansion(config);

  const { addWorkspace } = useWorkspaceCreation(setMessage, setSaving, async () => {
    await loadConfig();
    await loadWorkspaceNames();
  });

  const [showAddWorkspaceModal, setShowAddWorkspaceModal] = useState(false);
  const [showAddAspectModal, setShowAddAspectModal] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [newAspectData, setNewAspectData] = useState({
    aspectName: "",
    carryover: true,
    color: "#3B82F6",
    count_hours: true,
    count_plan: true,
    hours: 4,
    status: "Include",
    workspaceName: "",
  });

  useEffect(() => {
    void loadConfig();
    void loadWorkspaceNames();
  }, [loadConfig, loadWorkspaceNames]);

  const handleAddWorkspace = async () => {
    await addWorkspace(newWorkspaceName);
    setNewWorkspaceName("");
    setShowAddWorkspaceModal(false);
  };

  const handleAddAspect = async () => {
    await addAspect(
      newAspectData.workspaceName,
      newAspectData.aspectName,
      {
        carryover: newAspectData.carryover,
        color: newAspectData.color,
        count_hours: newAspectData.count_hours,
        count_plan: newAspectData.count_plan,
        hours: newAspectData.hours,
        status: newAspectData.status,
      },
      async () => {
        await loadConfig();
        expandWorkspace(newAspectData.workspaceName);
        setNewAspectData({
          aspectName: "",
          carryover: true,
          color: "#3B82F6",
          count_hours: true,
          count_plan: true,
          hours: 4,
          status: "Include",
          workspaceName: "",
        });
        setShowAddAspectModal(false);
      },
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">Workspace Management</h2>
          <p className="text-slate-400 mb-6">Verwalte deine Workspaces und deren Aspects.</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-6">
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">Workspace Management</h2>
          <p className="text-slate-400 mb-6">Verwalte deine Workspaces und deren Aspects.</p>
        </div>
        <div className="bg-slate-800 rounded-lg p-6">
          <p className="text-red-400">Fehler beim Laden der Konfiguration</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 border border-slate-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Workspace Management</h2>
            <p className="text-slate-400">
              Verwalte deine Workspaces und deren Aspects für optimales Time Tracking
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-slate-400">Workspaces</div>
            <div className="text-2xl font-bold text-blue-400">
              {Object.keys(config.workspaces).length}
            </div>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message ? (
        <div
          className={`p-4 rounded-xl border-2 ${
            message.type === "success"
              ? "bg-green-900/30 border-green-500/50 text-green-300"
              : "bg-red-900/30 border-red-500/50 text-red-300"
          }`}
        >
          <div className="flex items-center">
            <div
              className={`w-2 h-2 rounded-full mr-3 ${
                message.type === "success" ? "bg-green-400" : "bg-red-400"
              }`}
            />
            {message.text}
          </div>
        </div>
      ) : null}

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4">
        {/* Global Expand/Collapse Button */}
        {Object.keys(config.workspaces).some(
          (workspaceName) => Object.keys(config.workspaces[workspaceName] ?? {}).length > 4,
        ) ? (
          <button
            className="bg-slate-700/50 hover:bg-slate-700/70 border border-slate-600/50 hover:border-slate-600/70 text-slate-300 hover:text-white px-6 py-3 rounded-xl transition-all duration-200 font-medium"
            onClick={toggleAllWorkspaces}
          >
            <div className="flex items-center space-x-2">
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${
                  expandedWorkspaces.size > 0 ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M19 9l-7 7-7-7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
              <span>{expandedWorkspaces.size > 0 ? "Collapse All" : "Expand All"}</span>
            </div>
          </button>
        ) : null}

        {/* Add Workspace Button */}
        <button
          className="bg-blue-600/80 hover:bg-blue-600 border border-blue-500/50 hover:border-blue-500 text-white px-6 py-3 rounded-xl transition-all duration-200 font-medium"
          onClick={() => {
            setShowAddWorkspaceModal(true);
          }}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            <span>Add Workspace</span>
          </div>
        </button>

        {/* Add Aspect Button */}
        <button
          className="bg-green-600/80 hover:bg-green-600 border border-green-500/50 hover:border-green-500 text-white px-6 py-3 rounded-xl transition-all duration-200 font-medium"
          onClick={() => {
            setShowAddAspectModal(true);
          }}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            <span>Add Aspect</span>
          </div>
        </button>
      </div>

      {/* Workspaces Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(config.workspaces).map(([workspaceName, aspects]) => (
          <WorkspaceCard
            aspects={aspects}
            isExpanded={expandedWorkspaces.has(workspaceName)}
            key={workspaceName}
            onToggleBoolean={(aspectName, field) => {
              toggleAspectBoolean(workspaceName, aspectName, field);
            }}
            onToggleExpansion={() => {
              toggleWorkspaceExpansion(workspaceName);
            }}
            onUpdateColor={(aspectName, color) => {
              updateAspectColor(workspaceName, aspectName, color);
            }}
            onUpdateHours={(aspectName, hours) => {
              updateAspectHours(workspaceName, aspectName, hours);
            }}
            onUpdateStatus={(aspectName, status) => {
              updateAspectStatus(workspaceName, aspectName, status);
            }}
            workspaceName={workspaceName}
          />
        ))}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={saving}
          onClick={() => {
            void saveConfig();
          }}
        >
          {saving ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Speichern...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  d="M5 13l4 4L19 7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
              <span>Änderungen speichern</span>
            </div>
          )}
        </Button>
      </div>

      {/* Add Workspace Modal */}
      <AddWorkspaceModal
        isOpen={showAddWorkspaceModal}
        onAdd={handleAddWorkspace}
        onClose={() => {
          setShowAddWorkspaceModal(false);
          setNewWorkspaceName("");
        }}
        onWorkspaceNameChange={setNewWorkspaceName}
        saving={saving}
        workspaceName={newWorkspaceName}
      />

      {/* Add Aspect Modal */}
      <AddAspectModal
        aspectData={newAspectData}
        isOpen={showAddAspectModal}
        onAdd={handleAddAspect}
        onAspectDataChange={(data) => {
          setNewAspectData((previous) => ({ ...previous, ...data }));
        }}
        onClose={() => {
          setShowAddAspectModal(false);
          setNewAspectData({
            aspectName: "",
            carryover: true,
            color: "#3B82F6",
            count_hours: true,
            count_plan: true,
            hours: 4,
            status: "Include",
            workspaceName: "",
          });
        }}
        saving={saving}
        workspaceNames={workspaceNames}
      />
    </div>
  );
}
